import { useCallback, useState } from 'react'

import { TrainingCertificateDocumentIdsDocument } from 'generated/resources/graphql'

import { downloadBlob } from 'modules/core/lib/download'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { apiClient } from 'modules/core/lib/mobx-rest'

import { BulkSelection } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useBulkActionsConfig'

const downloadStatus = {
  idle: 'idle',
  downloading: 'downloading',
  noCertificates: 'no_certificates',
  error: 'error',
} as const

type DownloadStatus = (typeof downloadStatus)[keyof typeof downloadStatus]

interface UseDirectCertificateDownloadParams {
  trainingId: number
}

export const useDirectCertificateDownload = ({
  trainingId,
}: UseDirectCertificateDownloadParams) => {
  const [status, setStatus] = useState<DownloadStatus>(downloadStatus.idle)

  const { refetch } = useQuery(TrainingCertificateDocumentIdsDocument, {
    variables: {
      trainingId,
      excludedMembershipIds: [],
    },
    skip: true, // Don't fetch on mount, only on demand
  })

  const downloadCertificatesForSelection = useCallback(
    async (selection: BulkSelection) => {
      setStatus(downloadStatus.downloading)

      try {
        const excludedMembershipIds = selection.type === 'all' ? selection.excludedIds : []
        const filters = selection.type === 'all' ? selection.filters : undefined

        const result = await refetch({
          trainingId,
          excludedMembershipIds,
          classId: filters?.classId ?? null,
          dueDate: filters?.dueDate ?? null,
          search: filters?.search ?? null,
          status: filters?.status ?? null,
          teamId: filters?.teamId ?? null,
        })

        const documentIds =
          result.data?.trainings?.trainingMembershipFilesConnection?.nodes
            ?.map((node) => node.documentId)
            .filter((id): id is number => id != null) ?? []

        if (documentIds.length === 0) {
          setStatus(downloadStatus.noCertificates)
          return { success: false, reason: 'no_certificates' as const }
        }

        const response = await apiClient().post(
          '/documents/download_documents',
          { ids: documentIds.join(','), flat: true },
          { responseType: 'blob' }
        )

        if (!response || response.size === 0) {
          setStatus(downloadStatus.error)
          return { success: false, reason: 'error' as const }
        }

        const blob = new Blob([response], { type: 'application/zip' })
        downloadBlob(
          blob,
          `${i18n.t('trainings.trainings_revamp.training_detail.employees_table.certificates')}.zip`
        )
        setStatus(downloadStatus.idle)
        return { success: true }
      } catch {
        setStatus(downloadStatus.error)
        return { success: false, reason: 'error' as const }
      }
    },
    [refetch, trainingId]
  )

  const resetStatus = useCallback(() => setStatus(downloadStatus.idle), [])

  return {
    downloadCertificatesForSelection,
    resetStatus,
    isDownloading: status === downloadStatus.downloading,
    hasNoCertificates: status === downloadStatus.noCertificates,
    hasError: status === downloadStatus.error,
  }
}
